/**
 * @fileoverview ค่ากลางสำหรับโหลด Google Maps JavaScript API
 * ทุกจุดที่โหลดแผนที่ต้องใช้คีย์/id/libraries ชุดเดียวกัน ไม่งั้น script จะโหลดชนกัน
 * (Google ตอบ "This page didn't load Google Maps correctly")
 * @module utils/googleMaps
 */
import { useEffect, useState } from 'react';

// คีย์จริงตั้งไว้ที่ REACT_APP_API_MAP_KEY - REACT_APP_GOOGLE_MAPS_API_KEY ใช้เป็น fallback เท่านั้น
export const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_API_MAP_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

export const GOOGLE_MAPS_LOADER_ID = 'google-map-script';

// ต้องเป็น array ตัวเดียวกัน (reference เดียว) ในทุก loader
export const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry', 'drawing'];

// ---- สถานะ "Google ปฏิเสธคีย์" แบบกลาง ----
// Google เรียก window.gm_authFailure ครั้งเดียวตอนโหลด script จึงต้องตั้ง handler ตั้งแต่ระดับ module
// แล้วเก็บ flag ไว้ ให้ component ที่ mount ทีหลังอ่านสถานะได้
let mapsAuthFailed = false;
const authListeners = new Set();

const previousAuthFailure = typeof window !== 'undefined' ? window.gm_authFailure : undefined;
if (typeof window !== 'undefined') {
  window.gm_authFailure = () => {
    mapsAuthFailed = true;
    authListeners.forEach((listener) => listener(true));
    if (typeof previousAuthFailure === 'function') previousAuthFailure();
  };
}

export const isMapsAuthFailed = () => mapsAuthFailed;

export const subscribeMapsAuthFailed = (listener) => {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
};

/**
 * hook อ่านสถานะ Google ปฏิเสธคีย์ (อัปเดตทันทีเมื่อ gm_authFailure ยิง และถ้ายิงไปแล้วก่อน mount ก็ได้ค่า true)
 * @returns {boolean}
 */
export const useMapsAuthFailed = () => {
  const [failed, setFailed] = useState(mapsAuthFailed);
  useEffect(() => {
    setFailed(mapsAuthFailed);
    return subscribeMapsAuthFailed(setFailed);
  }, []);
  return failed;
};

/**
 * ลองโหลดแผนที่ใหม่ - ถ้า Google ปฏิเสธคีย์ google.maps ถูกสร้างแล้วเริ่มใหม่ในหน้าเดิมไม่ได้ จึง reload หน้า
 * กรณีอื่น (เครือข่ายล้ม) ลบ script ที่พังแล้วคืน false ให้ผู้เรียก mount loader ใหม่เอง
 * @returns {boolean} true = กำลัง reload หน้า
 */
export const retryLoadMaps = () => {
  if (mapsAuthFailed) {
    window.location.reload();
    return true;
  }
  const stale = document.getElementById(GOOGLE_MAPS_LOADER_ID);
  if (stale) stale.remove();
  if (window.google && !window.google.maps) delete window.google;
  return false;
};
